import React, { memo } from "react"
import { FormikProps } from "formik"
import { RegistrationPanelValidationType } from "./RegistrationPanelValidationSchema"
import { Button, Form, Input } from "antd"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { isSepaPaymentMethod } from "../../helpers/isSepaPaymentMethod"
import { definedInputValue } from "../common/helpers"
import { BookerPaymentMethodInput, PaymentTransferType, useGetBankDataQuery } from "../../generated/graphql"
import { v4 as uuid } from "uuid"
import { useLocation } from "react-router-dom"
import css from "./SepaData.module.css"

type SepaDataCreateProps = {
    formikProps: FormikProps<RegistrationPanelValidationType>
    useCase?: "registration" | "account"
    creatingNewSepa: boolean
    setCreatingNewSepa: (creatingNewSepa: boolean) => void
}

const SepaDataCreate: React.FC<SepaDataCreateProps> = ({ formikProps, useCase, creatingNewSepa, setCreatingNewSepa }) => {
    const values = formikProps.values
    const indexOfNewPaymentMethod = values?.paymentMethods?.length ?? 0
    const { handleNestedTextInputChange, handleNestedIbanInputChange, handleNestedBlur, nestedHelp } = createInputHelpers(formikProps)
    const indexOfCurrent = indexOfNewPaymentMethod === 0 ? 0 : indexOfNewPaymentMethod - 1
    // const indexOfCurrent = indexOfNewPaymentMethod - 1
    const nestedPath = `paymentMethods[${indexOfCurrent}]`

    const { data: bankData, loading: loadingBankData } = useGetBankDataQuery({
        skip: !values?.paymentMethods?.[indexOfCurrent]?.iban,
        variables: { iban: values?.paymentMethods?.[indexOfCurrent]?.iban ?? "" },
    })

    React.useEffect(() => {
        const bank = bankData?.getBankData.bankData?.name
        const bic = bankData?.getBankData.bankData?.bic
        if (bank && bic) {
            formikProps.setFieldValue(`${nestedPath}.bank`, bank)
            formikProps.setFieldValue(`${nestedPath}.bic`, bic)
            setTimeout(() => {
                formikProps.setFieldError(`${nestedPath}.iban`, undefined)
                formikProps.setFieldError(`${nestedPath}.bank`, undefined)
                formikProps.setFieldError(`${nestedPath}.bic`, undefined)
                formikProps.setFieldTouched(`${nestedPath}.iban`, true)
                formikProps.setFieldTouched(`${nestedPath}.bank`, true)
                formikProps.setFieldTouched(`${nestedPath}.bic`, true)
                setCreatingNewSepa(false)
            }, 300)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankData])

    const location = useLocation<{ openSepaCreate?: boolean }>()

    const createNewPaymentMethod = (sepaAccountholder?: string) => {
        const newPaymentMethod: BookerPaymentMethodInput = {
            id: uuid(),
            sepaAccountholder: sepaAccountholder,
            iban: undefined,
            bank: undefined,
            bic: undefined,
            type: PaymentTransferType.SepaDebit,
        }

        formikProps.setFieldValue(`paymentMethods[${indexOfNewPaymentMethod}]`, newPaymentMethod)
    }

    // Aus dem Zahlungsbereich ("Jetzt hinterlegen") kann der Create-Modus direkt
    // geöffnet werden, statt den Nutzer hier erneut klicken zu lassen.
    // Entscheidend ist "keine SEPA-Methode" — andere Zahlungsmethoden (z.B. Bar)
    // dürfen das automatische Öffnen nicht verhindern.
    React.useEffect(() => {
        if (location.state?.openSepaCreate && !creatingNewSepa && !values?.paymentMethods?.some(isSepaPaymentMethod)) {
            const holder = values?.firstname && values?.lastname ? `${values.firstname} ${values.lastname}` : undefined
            createNewPaymentMethod(holder)
            setCreatingNewSepa(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state])

    const getValidateStatus = (field: string): "" | "warning" | "error" | "success" | "validating" | undefined => {
        const isValidating = formikProps.isValidating
        // @ts-ignore
        const fieldError = formikProps.errors.paymentMethods?.[indexOfCurrent]?.[field]
        // @ts-ignore
        const isFieldTouched = formikProps.touched.paymentMethods?.[indexOfCurrent]?.[field]

        if (!isFieldTouched) {
            return undefined
        }

        if (fieldError) {
            return "error"
        }

        if (isValidating || loadingBankData) {
            return "validating"
        }

        if (!isValidating && !fieldError) {
            return "success"
        }

        return undefined
    }

    const resetSepaFields = () => {
        // Nur die in diesem Formular neu angelegte Methode entfernen, nie eine gespeicherte.
        const currentId = values?.paymentMethods?.[indexOfCurrent]?.id
        const isPersisted = formikProps.initialValues?.paymentMethods?.some((pm) => pm.id === currentId) ?? false
        if (!isPersisted) {
            formikProps.setFieldValue(
                "paymentMethods",
                values?.paymentMethods?.filter((_, index) => index !== indexOfCurrent)
            )
        }

        setCreatingNewSepa(false)
    }

    if (!creatingNewSepa) {
        const sepaAccountholder = values?.firstname && values?.lastname ? `${values?.firstname} ${values?.lastname}` : undefined

        return (
            <div className={css.noSepaPaymentMethod}>
                <div>Bisher wurden keine Lastschriftdaten hinterlegt</div>
                <Button
                    onClick={() => {
                        createNewPaymentMethod(sepaAccountholder)
                        setCreatingNewSepa(true)
                    }}
                >
                    Jetzt hinterlegen
                </Button>
            </div>
        )
    }

    return (
        <div key={`nestedPaymentMethod${indexOfCurrent}`} className={css.createContainer}>
            <Form.Item label='Kontoinhaber' help={nestedHelp(`paymentMethods[${indexOfCurrent}].sepaAccountholder`)} required validateStatus={getValidateStatus(`sepaAccountholder`)} hasFeedback>
                <Input
                    value={definedInputValue(values?.paymentMethods?.[indexOfCurrent]?.sepaAccountholder)}
                    onChange={handleNestedTextInputChange(`paymentMethods[${indexOfCurrent}].sepaAccountholder`)}
                    onBlur={handleNestedBlur(`paymentMethods[${indexOfCurrent}].sepaAccountholder`)}
                />
            </Form.Item>
            <Form.Item label='IBAN' help={nestedHelp(`${nestedPath}.iban`)} required hasFeedback validateStatus={getValidateStatus(`iban`)}>
                <Input
                    value={definedInputValue(values?.paymentMethods?.[indexOfCurrent]?.iban)}
                    onChange={handleNestedIbanInputChange(`${nestedPath}.iban`)}
                    onBlur={handleNestedBlur(`${nestedPath}.iban`)}
                />
            </Form.Item>
            <Form.Item
                label='Bank'
                help={nestedHelp(`${nestedPath}.bank`)}
                // required
                hasFeedback
                validateStatus={getValidateStatus(`bank`)}
            >
                <Input
                    value={definedInputValue(values?.paymentMethods?.[indexOfCurrent]?.bank)}
                    onChange={handleNestedTextInputChange(`${nestedPath}.bank`)}
                    onBlur={handleNestedBlur(`${nestedPath}.bank`)}
                />
            </Form.Item>
            <Form.Item
                label='Bic'
                help={nestedHelp(`${nestedPath}.bic`)}
                // required
                hasFeedback
                validateStatus={getValidateStatus(`bic`)}
            >
                <Input
                    value={definedInputValue(values?.paymentMethods?.[indexOfCurrent]?.bic)}
                    onChange={handleNestedTextInputChange(`${nestedPath}.bic`)}
                    onBlur={handleNestedBlur(`${nestedPath}.bic`)}
                />
            </Form.Item>
            {/* Auch in der Registrierung: Fehler der neuen Methode blockieren das Speichern, ohne
                Zurücksetzen gäbe es nach einem versehentlichen „Jetzt hinterlegen" keinen Ausweg. */}
            <div className={css.buttons}>
                <Button block type='ghost' onClick={() => resetSepaFields()} className={css.btnCancel}>
                    Zurücksetzen
                </Button>
            </div>
            {/* {useCase === "account" && (
                <div className={css.buttons}>
                    <Button block type='ghost' onClick={() => resetSepaFields()} className={css.btnCancel}>
                        Abbrechen
                    </Button>
                    <Button block type='primary' htmlType='submit' className={css.btnSave}>
                        Speichern
                    </Button>
                </div>
            )} */}
        </div>
    )
}

export default memo(SepaDataCreate)
